import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Icon, type IconName } from "@/components/ui/Icon";
import { FilterPanel } from "@/components/board/FilterPanel";
import { activeFilterCount } from "@/lib/boardFilters";
import type { ViewColumn } from "@/lib/boardView";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import type { FilterCond, SortSpec } from "@/lib/types";

export type BoardView = "table" | "kanban" | "calendar" | "timeline";

interface BoardToolbarProps {
  view: BoardView;
  setView: (view: BoardView) => void;
  /** Currently-grouped column id, or null. */
  groupByColumnId: string | null;
  /** Columns offered in the "Gruppieren nach" menu. */
  groupableColumns: { id: string; name: string }[];
  onGroupByChange: (columnId: string | null) => void;
  /** All columns (for the "Felder" show/hide menu). */
  allColumns: { id: string; name: string }[];
  hiddenColumnIds: string[];
  onToggleColumn: (columnId: string) => void;
  /** Filterable columns + the shared filter state. */
  filterColumns: ViewColumn[];
  filters: FilterCond[];
  onFiltersChange: (filters: FilterCond[]) => void;
  /** Sortable columns + the shared sort state. */
  sortColumns: ViewColumn[];
  sort: SortSpec | null;
  onSortChange: (sort: SortSpec | null) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  /** When set (ticket boards, write access), renders a "Neues Ticket" button. */
  onNewTicket?: () => void;
}

const VIEW_ITEMS: { id: BoardView; label: string; icon: IconName }[] = [
  { id: "table", label: t.table, icon: "layers" },
  { id: "kanban", label: t.kanban, icon: "kanban" },
  { id: "calendar", label: t.calendar, icon: "date" },
  { id: "timeline", label: t.timeline, icon: "sort" },
];

export function BoardToolbar({
  view,
  setView,
  groupByColumnId,
  groupableColumns,
  onGroupByChange,
  allColumns,
  hiddenColumnIds,
  onToggleColumn,
  filterColumns,
  filters,
  onFiltersChange,
  sortColumns,
  sort,
  onSortChange,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  onNewTicket,
}: BoardToolbarProps) {
  const [groupOpen, setGroupOpen] = useState(false);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const hiddenCount = hiddenColumnIds.length;
  const filterCount = activeFilterCount(filters);
  const sortName = sort ? (sortColumns.find((c) => c.id === sort.column_id)?.name ?? null) : null;
  const activeName = groupByColumnId
    ? (groupableColumns.find((c) => c.id === groupByColumnId)?.name ?? null)
    : null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "8px 14px",
        borderBottom: "1px solid var(--hairline-soft)",
      }}
    >
      <div
        role="tablist"
        style={{
          display: "flex",
          background: "var(--surface-soft)",
          borderRadius: 8,
          padding: 3,
        }}
      >
        {VIEW_ITEMS.map((v) => {
          const active = view === v.id;
          return (
            <button
              key={v.id}
              role="tab"
              aria-selected={active}
              onClick={() => setView(v.id)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "4px 10px",
                fontSize: 12.5,
                fontWeight: 500,
                background: active ? "var(--surface)" : "transparent",
                color: active ? "var(--ink-1)" : "var(--ink-3)",
                border: "none",
                borderRadius: 6,
                boxShadow: active ? "0 1px 2px rgba(13,62,99,0.06)" : "none",
                cursor: "pointer",
                fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
              }}
            >
              <Icon name={v.icon} size={12} /> {v.label}
            </button>
          );
        })}
      </div>
      <div style={{ width: 1, height: 18, background: "var(--hairline)", margin: "0 6px" }}></div>
      {onUndo ? (
        <>
          <Button
            variant="ghost"
            size="sm"
            icon="undo"
            title={`${t.undo} (Strg+Z)`}
            disabled={!canUndo}
            onClick={onUndo}
          />
          <Button
            variant="ghost"
            size="sm"
            icon="redo"
            title={`${t.redo} (Strg+Umschalt+Z)`}
            disabled={!canRedo}
            onClick={onRedo}
          />
          <div
            style={{ width: 1, height: 18, background: "var(--hairline)", margin: "0 6px" }}
          ></div>
        </>
      ) : null}
      {/* Filter — shared per-column conditions (status/text/checkbox/date). */}
      <div style={{ position: "relative" }}>
        <Button
          variant={filterCount > 0 ? "secondary" : "ghost"}
          size="sm"
          icon="filter"
          onClick={() => setFilterOpen((o) => !o)}
        >
          {filterCount > 0 ? `${t.filter} (${filterCount})` : t.filter}
        </Button>
        {filterOpen ? (
          <>
            <div
              onClick={() => setFilterOpen(false)}
              style={{ position: "fixed", inset: 0, zIndex: 60 }}
            />
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                left: 0,
                zIndex: 61,
                // Comfortable panel that grows with content: ~80vh tall, a roomy
                // width that stays within the viewport on mobile. overflowX:hidden
                // + box-sizing kill the stray horizontal scrollbar.
                width: "min(480px, 94vw)",
                maxHeight: "80vh",
                boxSizing: "border-box",
                overflowY: "auto",
                overflowX: "hidden",
                background: "var(--surface-elev)",
                border: "1px solid var(--hairline-strong)",
                borderRadius: 9,
                boxShadow: "0 10px 30px rgba(13,62,99,0.16)",
                padding: 12,
              }}
            >
              <FilterPanel columns={filterColumns} filters={filters} onChange={onFiltersChange} />
            </div>
          </>
        ) : null}
      </div>
      {/* Sort — shared single-column sort (click a column to cycle asc/desc/off). */}
      <div style={{ position: "relative" }}>
        <Button
          variant={sortName ? "secondary" : "ghost"}
          size="sm"
          icon="sort"
          onClick={() => setSortOpen((o) => !o)}
        >
          {sortName ? `${t.sort}: ${sortName} ${sort?.dir === "desc" ? "↓" : "↑"}` : t.sort}
        </Button>
        {sortOpen ? (
          <>
            <div
              onClick={() => setSortOpen(false)}
              style={{ position: "fixed", inset: 0, zIndex: 60 }}
            />
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                left: 0,
                zIndex: 61,
                minWidth: 200,
                maxHeight: 300,
                overflowY: "auto",
                background: "var(--surface-elev)",
                border: "1px solid var(--hairline-strong)",
                borderRadius: 9,
                boxShadow: "0 10px 30px rgba(13,62,99,0.16)",
                padding: 5,
              }}
            >
              <GroupMenuItem
                label={t.sortNone}
                active={!sort}
                onClick={() => {
                  onSortChange(null);
                  setSortOpen(false);
                }}
              />
              {sortColumns.length > 0 ? (
                <div style={{ height: 1, background: "var(--hairline)", margin: "5px 4px" }} />
              ) : null}
              {sortColumns.map((c) => {
                const active = sort?.column_id === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => {
                      if (!active) onSortChange({ column_id: c.id, dir: "asc" });
                      else if (sort?.dir === "asc") onSortChange({ column_id: c.id, dir: "desc" });
                      else onSortChange(null);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      width: "100%",
                      padding: "7px 9px",
                      fontSize: 13,
                      textAlign: "left",
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      background: active ? "var(--surface-soft)" : "transparent",
                      color: "var(--ink-1)",
                      fontWeight: active ? 600 : 400,
                      fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
                    }}
                  >
                    {c.name}
                    {active ? <span>{sort?.dir === "desc" ? "↓" : "↑"}</span> : null}
                  </button>
                );
              })}
            </div>
          </>
        ) : null}
      </div>

      {/* Group-by picker — the only wired toolbar control. */}
      <div style={{ position: "relative" }}>
        <Button
          variant={activeName ? "secondary" : "ghost"}
          size="sm"
          icon="group"
          onClick={() => setGroupOpen((o) => !o)}
        >
          {activeName ? `${t.group}: ${activeName}` : t.group}
        </Button>
        {groupOpen ? (
          <>
            <div
              onClick={() => setGroupOpen(false)}
              style={{ position: "fixed", inset: 0, zIndex: 60 }}
            />
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                left: 0,
                zIndex: 61,
                minWidth: 200,
                maxHeight: 280,
                overflowY: "auto",
                background: "var(--surface-elev)",
                border: "1px solid var(--hairline-strong)",
                borderRadius: 9,
                boxShadow: "0 10px 30px rgba(13,62,99,0.16)",
                padding: 5,
              }}
            >
              <GroupMenuItem
                label={t.groupByNone}
                active={!groupByColumnId}
                onClick={() => {
                  onGroupByChange(null);
                  setGroupOpen(false);
                }}
              />
              {groupableColumns.length > 0 ? (
                <div style={{ height: 1, background: "var(--hairline)", margin: "5px 4px" }} />
              ) : null}
              {groupableColumns.map((c) => (
                <GroupMenuItem
                  key={c.id}
                  label={c.name}
                  active={groupByColumnId === c.id}
                  onClick={() => {
                    onGroupByChange(c.id);
                    setGroupOpen(false);
                  }}
                />
              ))}
            </div>
          </>
        ) : null}
      </div>

      {/* Column show/hide picker — the board's shared visible-column set. */}
      <div style={{ position: "relative" }}>
        <Button
          variant={hiddenCount > 0 ? "secondary" : "ghost"}
          size="sm"
          icon="eye"
          onClick={() => setFieldsOpen((o) => !o)}
        >
          {hiddenCount > 0 ? `${t.fields} (${hiddenCount} ausgeblendet)` : t.fields}
        </Button>
        {fieldsOpen ? (
          <>
            <div
              onClick={() => setFieldsOpen(false)}
              style={{ position: "fixed", inset: 0, zIndex: 60 }}
            />
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                left: 0,
                zIndex: 61,
                minWidth: 220,
                maxHeight: 320,
                overflowY: "auto",
                background: "var(--surface-elev)",
                border: "1px solid var(--hairline-strong)",
                borderRadius: 9,
                boxShadow: "0 10px 30px rgba(13,62,99,0.16)",
                padding: 5,
              }}
            >
              {allColumns.map((c) => (
                <FieldToggleItem
                  key={c.id}
                  label={c.name}
                  visible={!hiddenColumnIds.includes(c.id)}
                  onClick={() => onToggleColumn(c.id)}
                />
              ))}
            </div>
          </>
        ) : null}
      </div>
      <div style={{ flex: 1 }}></div>
      {onNewTicket ? (
        <Button variant="primary" size="sm" icon="plus" onClick={onNewTicket}>
          {t.newTicket}
        </Button>
      ) : null}
      <Button
        variant="ghost"
        size="sm"
        icon="search"
        title={t.comingSoon}
        onClick={() => showToast(t.comingSoon)}
      >
        {t.searchRows}
      </Button>
    </div>
  );
}

function GroupMenuItem({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        width: "100%",
        padding: "7px 9px",
        fontSize: 13,
        textAlign: "left",
        border: "none",
        borderRadius: 6,
        cursor: "pointer",
        background: active ? "var(--surface-soft)" : "transparent",
        color: "var(--ink-1)",
        fontWeight: active ? 600 : 400,
        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
      }}
    >
      {label}
      {active ? <Icon name="check" size={13} /> : null}
    </button>
  );
}

function FieldToggleItem({
  label,
  visible,
  onClick,
}: {
  label: string;
  visible: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        width: "100%",
        padding: "7px 9px",
        fontSize: 13,
        textAlign: "left",
        border: "none",
        borderRadius: 6,
        cursor: "pointer",
        background: "transparent",
        color: visible ? "var(--ink-1)" : "var(--ink-4)",
        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
      }}
    >
      <span
        style={{
          width: 16,
          height: 16,
          flexShrink: 0,
          borderRadius: 4,
          border: `1.5px solid ${visible ? "var(--color-brand-blue)" : "var(--hairline-strong)"}`,
          background: visible ? "var(--color-brand-blue)" : "transparent",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
        }}
      >
        {visible ? <Icon name="check" size={11} /> : null}
      </span>
      {label}
    </button>
  );
}
